import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { data } from '@portal/pkg-api/shared'
import { EntityApi, EntityLegacyApi } from '@portal/pkg-api/navigator'
import { useEntityTypes } from '@navigator/data/EntityType'
import { EntityAncestorPathItem, EntityType } from '@portal/pkg-api/gen/navigator'
import { useEffectiveUser } from '@shared/data'
import { Filter } from '@portal/pkg-api/gen/legacy'
import { getUserNotificationStore } from '@migrate/stores'
import { UserNotificationType } from '@migrate/stores/UserNotificationStore'

const prefix = 'navigator.entity'

export const EntityQueries = {
  entity: (entityId: number) => ({
    queryKey: [`${prefix}.entity`, entityId],
    queryFn: () => data(EntityApi.getEntity(entityId)),
    // Configure this specific query to refetch data more often as it informs
    // several UI concerns regarding calculating/locked status, etc.
    refetchInterval: 1000 * 30, // 30 seconds
  }),
  isNonFrozen: (entityId?: number) => ({
    queryKey: [`${prefix}.isNonFrozen`, entityId],
    queryFn: () => data(EntityApi.isNonFrozen(entityId!)),
    enabled: !!entityId,
  }),
  entityAncestors: (entityId: number) => ({
    queryKey: [`${prefix}.entityAncestors`, entityId],
    queryFn: () => data(EntityApi.getEntityAncestors(entityId)),
  }),
  detailsViews: (entityTypeId?: number, orgId?: number) => ({
    queryKey: [`${prefix}.detailsViews`, entityTypeId, orgId],
    queryFn: async () => {
      const response = await data(EntityLegacyApi.getDetailsViews(orgId!, entityTypeId!))
      return response.data
    },
    enabled: !!orgId && !!entityTypeId,
  }),
  enabledEntityIds: (entityTypeId: number, entityIds?: number[]) => ({
    queryKey: [`${prefix}.activeCheck`, entityTypeId, entityIds],
    queryFn: async () => {
      const response = await data(EntityApi.enabledEntityIds(entityIds, 0))
      return new Set(response)
    },
    refetchInterval: 1000 * 10, // 10 seconds
  }),
}

export function useEntity(entityId: number) {
  return useQuery(EntityQueries.entity(entityId))
}

export function useIsNonFrozen(entityId?: number) {
  return useQuery(EntityQueries.isNonFrozen(entityId))
}

export function useEntityAncestors(entityId: number) {
  return useQuery(EntityQueries.entityAncestors(entityId))
}

export interface EntityAncestorWithType extends EntityAncestorPathItem {
  entityType?: EntityType
}

export function useEntityAncestorsWithEntityType(entityId: number) {
  const { data: ancestors } = useEntityAncestors(entityId)
  const { data: entityTypes } = useEntityTypes()
  return useQuery<ReadonlyArray<EntityAncestorWithType>>({
    queryKey: [`${prefix}.entityAncestorsWithEntityType`, entityId],
    queryFn: () =>
      ancestors!.map((ancestor) => ({
        ...ancestor,
        entityType: entityTypes!.find(({ id }) => id === ancestor.entityTypeId),
      })),
    enabled: !!entityTypes && !!ancestors,
  })
}

export function useDetailsViews(entityTypeId?: number) {
  const effectiveUser = useEffectiveUser()

  return useQuery(EntityQueries.detailsViews(entityTypeId, effectiveUser?.orgId))
}

export function useDeleteView() {
  const queryClient = useQueryClient()
  const effectiveUser = useEffectiveUser()

  return useMutation<unknown, unknown, Filter>({
    mutationFn: (view) => EntityLegacyApi.deleteDetailsView(view, { mercatusNotifyOnError: false }),
    onSuccess: (_data, view) => {
      void queryClient.invalidateQueries(
        EntityQueries.detailsViews(view.entityTypeId, effectiveUser?.orgId),
      )
      getUserNotificationStore().addNotification(
        UserNotificationType.SUCCESS,
        'navigator.detail.views.delete',
      )
    },
    onError: (error: any) => {
      getUserNotificationStore().addNotification(UserNotificationType.ERROR, error.message)
    },
  })
}
